import React, { Fragment } from 'react';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { CustomCategoryStyles, CustomFolderStyles } from '../page/CategoryFolderTitle';
import { EVENTS, CATEGORY_VISIBILITY } from '../../utilities/CONSTANTS';
import { canCreateCategory } from '../../utilities/permissionsUtilities';

const INT_REGEX = /^\d+$/;

const isValidNewOption = (inputValue, selectValue, selectOptions) => inputValue
    && selectOptions.every(({ label }) => label.toLowerCase() !== inputValue.toLowerCase()) && !INT_REGEX.test(inputValue);

const mapToFolderSelectOption = ({
    name: label, id: value, color, icon, parent_folder_id,
}) => ({
    label, value, color, icon, parent_folder_id,
});

const getSubcategoryOptions = (flatFolders, parentFolderId, excludeFolder) => flatFolders
    .filter(folder => (folder.parent_folder_id === parentFolderId && folder.id !== excludeFolder))
    .map(folder => mapToFolderSelectOption(folder));

const SubcategorySelectorItem = (props) => {
    const { category, folder, excludeFolder } = props;
    const options = getSubcategoryOptions(category.folders, folder.parent_folder_id || null, excludeFolder);

    return (
        options.length > 0
            && (
                <Fragment>
                    <label className="bold block col-12 mb1">Choose a subcategory</label>
                    <Select
                        id={`subcategory-${folder.parent_folder_id || 0}`}
                        value={folder}
                        styles={CustomFolderStyles}
                        className={props.selectCss}
                        placeholder="Choose a subcategory"
                        options={options}
                        onChange={props.onSelect}
                    />
                </Fragment>
            )
    );
};

class CategoryAndFolderSelector extends React.Component {
    state = {
        category: null,
        folder: null,
        parents: [],
        categoriesAndFolders: [],
    };

    componentDidMount() {
        if (window.categoriesAndFolders) {
            this.setInitialState();
        } else {
            window.addEventListener(EVENTS.CATEGORIES_LOADED, this.setInitialState);
        }
    }

    componentWillUnmount() { return window.removeEventListener(EVENTS.CATEGORIES_LOADED, this.setInitialState); }

    setInitialState = () => {
        let { category, folder } = this.state;
        const parents = [];
        const { initialCategoryId = null, initialFolderId = null } = this.props;
        const categoriesAndFolders = window.categoriesAndFolders
            .map(({
                name: label, id: value, folders, color, icon, visibility, external_sites,
            }) => ({
                label, value, folders, color, icon, visibility, external_sites_count: external_sites.length,
            }));
        if (initialCategoryId) {
            category = categoriesAndFolders.find(category => (category.value === initialCategoryId));
            if (initialFolderId) {
                folder = mapToFolderSelectOption(category.folders.find(folder => (folder.id === initialFolderId)));

                let parent_id = folder.parent_folder_id;

                while (parent_id) {
                    parent = mapToFolderSelectOption(category.folders.find(folder => (folder.id === parent_id)));
                    parents.unshift(parent);

                    parent_id = parent.parent_folder_id;
                }
            }
        }
        return this.setState({
            category, folder, parents, categoriesAndFolders, initialCategoryId,
        });
    };

    onSelectCategory = (category) => {
        if (category === this.state.category) return;

        const parents = [];
        const folder = null;

        if (this.props.setCategoryId) {
            this.props.setCategoryId(category && category.value);
        }

        if (this.props.setFolderId) {
            this.props.setFolderId(folder);
        }

        return this.setState({ category, folder, parents });
    };

    onSelectFolder = (newFolder) => {
        let { parents } = this.state;
        let folder = newFolder;

        if (folder === null) { // the last input field was cleared
            const previousFolder = this.state.folder;

            const { parents } = this.state;

            if (previousFolder.parent_folder_id) {
                folder = parents.pop();
            }
        } else {
            parents = [];
            let parent_id = folder.parent_folder_id;

            while (parent_id) {
                parent = mapToFolderSelectOption(this.state.category.folders.find(folder => (folder.id === parent_id)));
                parents.unshift(parent);
                parent_id = parent.parent_folder_id;
            }
        }

        if (this.props.setFolderId) {
            this.props.setFolderId(folder && folder.value);
        }

        return this.setState({ folder, parents });
    };

    render() {
        const {
            selectOnly,
            hideFolders,
            smallWidth,
            allowUncategorize = true,
            excludeCategory = 0,
            excludeFolder = 0,
            uncategorizedPlaceholder = 'Uncategorized',
            excludeExternalSites = false,
        } = this.props;
        const {
            category, folder, parents, categoriesAndFolders, initialCategoryId,
        } = this.state;

        const cannotCreate = selectOnly ? true : !canCreateCategory();

        const SelectComponent = cannotCreate ? Select : CreatableSelect;
        const categoryLabel = cannotCreate ? 'Choose a category' : 'Choose or create a category';
        const selectCss = smallWidth ? 'block col-12 field mb2 lg-col-6' : 'block col-12 field mb2';
        const showCategoryWarning = category ? CATEGORY_VISIBILITY.PUBLIC_VISIBLE === category.visibility : false;

        const showFolderSelector = !hideFolders && category && !category.__isNew__
            && category.folders && category.folders.filter(f => (f.id !== excludeFolder)).length > 0;
        let lastLevel = folder
            && category.folders.filter(subfolder => (subfolder.parent_folder_id === folder.value && subfolder.id !== excludeFolder));

        if (this.props.selectedFolders && lastLevel) {
            lastLevel = lastLevel.filter(s => !this.props.selectedFolders.includes(s.id));
        }

        const showLastLevelSelector = lastLevel && lastLevel.length !== 0;
        let categoryOptions = excludeCategory
            ? categoriesAndFolders.filter(c => (c.value !== excludeCategory))
            : categoriesAndFolders;

        if (excludeExternalSites) {
            categoryOptions = categoriesAndFolders.filter(c => (
                c.external_sites_count === 0 // exclude categories that have external sites
                || (c.external_sites_count > 0 && c.value === initialCategoryId) // or the currently selected category (when editing existing external site)
            ));
        }

        let subcategoryOptions = showFolderSelector
            ? getSubcategoryOptions(category.folders, folder && folder.parent_folder_id || null, excludeFolder)
            : [];

        if (this.props.selectedFolders && subcategoryOptions) {
            subcategoryOptions = subcategoryOptions.filter(s => !this.props.selectedFolders.includes(s.value));
        }

        return (
            <Fragment>
                <label htmlFor="category_id" className="bold block col-12 mb1">{categoryLabel}</label>
                <SelectComponent
                    id="category_id"
                    name="category_id"
                    value={category}
                    styles={CustomCategoryStyles}
                    className={selectCss}
                    placeholder={uncategorizedPlaceholder}
                    options={categoryOptions}
                    onChange={this.onSelectCategory}
                    isValidNewOption={isValidNewOption}
                    isClearable={allowUncategorize}
                />
                { showFolderSelector
                    && (
                        <Fragment>
                            {
                                parents.map(subcategory => (
                                    <SubcategorySelectorItem
                                        category={category}
                                        folder={subcategory}
                                        selectCss={selectCss}
                                        onSelect={this.onSelectFolder}
                                        key={`subcategory-select-${subcategory.value}`}
                                        excludeFolder={excludeFolder}
                                    />
                                ))
                            }
                        </Fragment>
                    )}
                { subcategoryOptions.length > 0
                    && (
                        <Fragment>
                            <label htmlFor="folder_id" className="bold block col-12 mb1">Choose a subcategory</label>
                            <Select
                                id="folder_id"
                                name="folder_id"
                                value={folder}
                                styles={CustomFolderStyles}
                                className={selectCss}
                                placeholder={categoriesAndFolders.length ? 'Choose a subcategory' : 'Loading...'}
                                options={subcategoryOptions}
                                onChange={this.onSelectFolder}
                                isValidNewOption={isValidNewOption}
                                isClearable
                            />
                        </Fragment>
                    )}
                { showLastLevelSelector
                    && (
                        <Fragment>
                            <label className="bold block col-12 mb1">Choose a subcategory</label>
                            <Select
                                id={`subcategory-${folder.value}`}
                                value={null}
                                styles={CustomFolderStyles}
                                className={selectCss}
                                placeholder="Choose a subcategory"
                                options={lastLevel.map(folder => mapToFolderSelectOption(folder))}
                                onChange={this.onSelectFolder}
                                isClearable
                            />
                        </Fragment>
                    )}
                { showCategoryWarning
                    && (
                        <div className="info-message rounded" style={{ padding: '8px' }}>
                            <img className="message-icon" height="28" width="28" src="/img/icons/icon-visibility-public.png" />
                            <span className="message-text">This item will be publicly accessible in this category</span>
                        </div>
                    )}
            </Fragment>
        );
    }
}

export default CategoryAndFolderSelector;
